import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  Building2, 
  Shield, 
  TrendingUp, 
  FileText, 
  Users, 
  Sparkles,
  ChevronRight,
  Check,
  MapPin,
  BarChart3
} from "lucide-react";

const LandingPage = () => {
  const features = [
    {
      icon: Building2,
      title: "Asset Dashboard",
      description: "Monitor all your properties from one centralized dashboard with real-time value snapshots"
    },
    {
      icon: FileText,
      title: "Documentation Tracker",
      description: "Track C of O, deeds, surveys, and all property documents with status indicators"
    },
    {
      icon: Shield,
      title: "Lease Management",
      description: "Manage tenants, track rent, and receive automated alerts before leases expire"
    },
    {
      icon: Sparkles,
      title: "AI-Assisted Appraisal",
      description: "Get intelligent property valuations based on location, type, and market data"
    },
    {
      icon: TrendingUp,
      title: "Performance Metrics",
      description: "Track rental income, maintenance costs, and net performance per asset"
    },
    {
      icon: Users,
      title: "Professional Services",
      description: "Access property management, valuation, and documentation services"
    }
  ];

  const plans = [
    {
      name: "Free",
      price: "₦0",
      period: "/month",
      features: ["Up to 2 assets", "Basic dashboard", "Manual valuations", "Email alerts"],
      highlighted: false
    },
    {
      name: "Professional",
      price: "₦15,000",
      period: "/month",
      features: ["Up to 20 assets", "AI-assisted appraisal", "Full document tracking", "Lease alerts", "Marketplace access"],
      highlighted: true
    },
    {
      name: "Enterprise",
      price: "₦50,000",
      period: "/month",
      features: ["Unlimited assets", "Portfolio analytics", "Multi-user roles", "Priority support", "Custom reports"],
      highlighted: false
    }
  ];

  return (
    <div className="min-h-screen bg-slate-950 relative overflow-hidden">
      {/* Noise Overlay */}
      <div className="noise-overlay absolute inset-0 pointer-events-none"></div>
      
      {/* Hero Glow */}
      <div className="hero-glow absolute inset-0 pointer-events-none"></div>

      {/* Navigation */}
      <nav className="relative z-10 glass sticky top-0">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-white">Blue Element</span>
            </Link>
            
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-slate-400 hover:text-white transition-colors">Features</a>
              <a href="#pricing" className="text-slate-400 hover:text-white transition-colors">Pricing</a>
              <Link to="/login" className="text-slate-400 hover:text-white transition-colors">Login</Link>
              <Link to="/register">
                <Button className="btn-primary" data-testid="nav-get-started-btn">Get Started</Button>
              </Link>
            </div>

            <Link to="/register" className="md:hidden">
              <Button className="btn-primary" size="sm" data-testid="mobile-get-started-btn">Get Started</Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative z-10 py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8 fade-in">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/30 text-primary text-sm">
                <Sparkles className="w-4 h-4" />
                AI-Powered Asset Intelligence
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight">
                Manage Your <span className="gradient-text">Nigerian Assets</span> with Intelligence
              </h1>
              
              <p className="text-lg text-slate-400 max-w-xl">
                A unified digital platform for managing individual, corporate, and estate assets with visibility, professional support, and AI-powered insights.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/register">
                  <Button className="btn-primary text-lg h-14 px-10" data-testid="hero-get-started-btn">
                    Start Free Trial
                    <ChevronRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <a href="#features">
                  <Button variant="ghost" className="btn-secondary text-lg h-14 px-10" data-testid="hero-learn-more-btn">
                    Learn More
                  </Button>
                </a>
              </div>
              
              <div className="flex items-center gap-8 pt-4">
                <div className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-primary" />
                  <span className="text-slate-400 text-sm">Nigeria-Focused</span>
                </div>
                <div className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-cyan-500" />
                  <span className="text-slate-400 text-sm">AI Appraisals</span>
                </div>
                <div className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-emerald-500" />
                  <span className="text-slate-400 text-sm">Secure</span>
                </div>
              </div>
            </div>
            
            <div className="relative fade-in-delay-2">
              <div className="relative rounded-3xl overflow-hidden border border-slate-800 shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1640475168764-b0a8860c0fcf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBsYWdvcyUyMGNpdHklMjBza3lsaW5lJTIwbmlnaHR8ZW58MHx8fHwxNzY5MjgyNjkwfDA&ixlib=rb-4.1.0&q=85"
                  alt="Lagos Skyline"
                  className="w-full h-auto object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/50 to-transparent"></div>
                
                {/* Floating Stats Card */}
                <div className="absolute bottom-6 left-6 right-6 glass rounded-2xl p-4">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-2xl font-bold text-white">₦2.5B+</p>
                      <p className="text-xs text-slate-400">Assets Managed</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-primary">1,200+</p>
                      <p className="text-xs text-slate-400">Properties</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-cyan-500">98%</p>
                      <p className="text-xs text-slate-400">Satisfaction</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Decorative Elements */}
              <div className="absolute -z-10 top-10 -right-10 w-72 h-72 bg-primary/20 rounded-full blur-3xl"></div>
              <div className="absolute -z-10 -bottom-10 -left-10 w-72 h-72 bg-cyan-500/20 rounded-full blur-3xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="relative z-10 py-24 bg-slate-900/30">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Everything You Need to Manage Your Assets
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Comprehensive tools designed specifically for Nigerian property owners, estates, and corporations.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div 
                key={`feature-${feature.title}`} 
                className="card-dashboard group"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                  <feature.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-slate-400">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="relative z-10 py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Choose the plan that fits your portfolio. Upgrade anytime as you grow.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {plans.map((plan, index) => (
              <div 
                key={plan.name} 
                className={`relative rounded-3xl p-8 ${
                  plan.highlighted 
                    ? 'bg-primary/10 border-2 border-primary glow' 
                    : 'bg-slate-900/50 border border-slate-800'
                }`}
              >
                {plan.highlighted && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-primary rounded-full text-sm font-medium text-white">
                    Most Popular
                  </div>
                )}
                
                <h3 className="text-xl font-semibold text-white mb-2">{plan.name}</h3>
                <div className="flex items-baseline gap-1 mb-6">
                  <span className="text-4xl font-bold text-white">{plan.price}</span>
                  <span className="text-slate-400">{plan.period}</span>
                </div>
                
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, idx) => (
                    <li key={`${plan.id}-feature-${idx}`} className="flex items-center gap-3 text-slate-300">
                      <Check className="w-5 h-5 text-primary flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
                
                <Link to="/register">
                  <Button 
                    className={`w-full ${plan.highlighted ? 'btn-primary' : 'btn-secondary'}`}
                    data-testid={`pricing-${plan.name.toLowerCase()}-btn`}
                  >
                    Get Started
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative z-10 py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="card-feature text-center relative overflow-hidden">
            <div className="absolute inset-0 hero-glow pointer-events-none"></div>
            
            <div className="relative z-10">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Ready to Take Control of Your Assets?
              </h2>
              <p className="text-slate-400 max-w-2xl mx-auto mb-8">
                Join thousands of Nigerian property owners who trust Blue Element to manage their portfolios.
              </p>
              <Link to="/register">
                <Button className="btn-primary text-lg h-14 px-10" data-testid="cta-get-started-btn">
                  Start Your Free Trial
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t border-slate-800 py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                <Building2 className="w-5 h-5 text-white" />
              </div>
              <span className="font-semibold text-white">Blue Element</span>
            </div>
            
            <p className="text-slate-400 text-sm">
              © 2025 Blue Element. All rights reserved.
            </p>
            
            <div className="flex items-center gap-6">
              <a href="#" className="text-slate-400 hover:text-white text-sm transition-colors">Privacy</a>
              <a href="#" className="text-slate-400 hover:text-white text-sm transition-colors">Terms</a>
              <a href="#" className="text-slate-400 hover:text-white text-sm transition-colors">Contact</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
